import React from 'react';
import ReportCreateNewLabelField from '../ReportCreateNewLabelField';
import componentConstants from '../../../core-utils/messages/componentConstants';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useReportsCohortItem } from '../../../core-utils/custom-hooks/useReportsCohortItem';
import SvgRefresh from '../Icons/Refresh';
import SvgDelete from '../Icons/SvgDelete';
import { ToggleGroup } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { CohortPropertyField } from './CohortPropertyField';
import { useStores } from '../../../stores';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';

interface ReportsCohortItemProps {
  totalResults: number;
  handleDeleteCohort: (_index: number, _refresh?: boolean) => void;
  type?: string;
  existingMemberUniqueIds?: string[];
}
export const commonDropDownProps = {
  selectStyles: {
    height: '36px',
    '& >.MuiOutlinedInput-input': {
      padding: '6px 16px',
      fontSize: '14px',
      lineHeight: '24px',
      color: patientAccessTheme.palette.text.highemphasis,
    },
  },
  labelTop: '10px',
};

const toggleStyles = {
  color: patientAccessTheme.palette.status.white,
  background: patientAccessTheme.palette.primary[500],
  fontWeight: 600,
  fontSize: '12px',
  paddingRight: '14px',
};

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: '8px',
  },
  cohortItem: {
    display: 'flex',
    alignItems: 'center',
  },
  inputFieldsGrid: {
    width: '656px',
    background: '#FBFBFE',
    padding: '16px 20px',
    borderRadius: '8px',
  },
  resultsGrid: {
    display: 'flex',
    justifyContent: 'center',
    padding: '10px 26px',
    width: '240px',
    borderRadius: '8px',
    backgroundColor: '#E9EFFF',
  },
  lineAndResultsGrid: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    height: '154px',
  },
});

const ReportsCohortItem = observer(
  ({
    totalResults,
    handleDeleteCohort,
    type,
    existingMemberUniqueIds,
  }: ReportsCohortItemProps) => {
    const constants = componentConstants.ATOMS.COHORT_DETAILS;

    const { classes } = useStyles();

    const cohortDetailsStore = useStores().cohortDetailsStore;

    const categoryDropDownOptions = toJS(
      cohortDetailsStore.categoryDropDownOptions,
    );
    const conditionDropDownOptions = toJS(
      cohortDetailsStore.conditionDropDownOptions,
    );

    const {
      cohortDetailsList,
      loadingPropertiesByIndex,
      handleToggleChange,
      handleToggleList,
      handleCohortChange,
      handleScrollEvent,
      handleTextFieldForProperties,
    } = useReportsCohortItem({
      totalResults,
      type,
      existingMemberUniqueIds,
    });

    return (
      <Box>
        {cohortDetailsList.map((cohort, index) => (
          <Grid container className={classes.root} key={cohort.id}>
            <Grid container className={classes.cohortItem}>
              <Grid className={classes.inputFieldsGrid}>
                <ReportCreateNewLabelField
                  label={constants.CATEGORY}
                  width="100%"
                  component={
                    <Dropdown
                      label={constants.SELECT}
                      dropDownOptions={categoryDropDownOptions ?? []}
                      dropDownValues={categoryDropDownOptions ?? []}
                      value={cohort.category}
                      handleChange={(e) =>
                        handleCohortChange(index, e.target.value, 'category')
                      }
                      selectStyles={{
                        ...commonDropDownProps.selectStyles,
                        width: '300px',
                      }}
                      labelTop="10px"
                    />
                  }
                />

                <Grid
                  container
                  sx={{
                    alignItems: 'flex-end',
                  }}
                >
                  <Grid style={{ marginRight: '20px' }}>
                    <ReportCreateNewLabelField
                      label={constants.CONDITION}
                      component={
                        <Dropdown
                          label="Select"
                          dropDownOptions={
                            conditionDropDownOptions[
                              cohort.dropdownSelectionTypeIndex
                            ] ?? []
                          }
                          dropDownValues={
                            conditionDropDownOptions[
                              cohort.dropdownSelectionTypeIndex
                            ] ?? []
                          }
                          value={cohort.condition}
                          handleChange={(e) =>
                            handleCohortChange(
                              index,
                              e.target.value,
                              'condition',
                            )
                          }
                          selectStyles={{
                            ...commonDropDownProps.selectStyles,
                            width: '220px',
                          }}
                          labelTop="10px"
                        />
                      }
                    />
                  </Grid>

                  {/* ✅ 3-way property logic lives in its own component */}
                  <Grid style={{ marginRight: '20px' }}>
                    <CohortPropertyField
                      cohort={cohort}
                      index={index}
                      loadingPropertiesByIndex={loadingPropertiesByIndex}
                      onSingleChange={(i, val) =>
                        handleCohortChange(i, val, 'property')
                      }
                      onMultipleChange={(i, val) =>
                        handleCohortChange(i, val, 'multipleProperties')
                      }
                      onTextChange={(i, val) =>
                        handleCohortChange(i, val, 'property')
                      }
                      onScrollEvent={(c) =>
                        handleScrollEvent(
                          c.category,
                          index,
                          c.dropdownSelectionTypeIndex,
                          c.propertiesPageNumber,
                          c.totalPagesForProperties,
                        )
                      }
                      onSearchChange={(e, c, i) =>
                        handleTextFieldForProperties(
                          e,
                          c.category,
                          i,
                          c.dropdownSelectionTypeIndex,
                        )
                      }
                    />
                  </Grid>

                  {/* Actions */}
                  <Grid>
                    <SvgRefresh
                      height="24px"
                      width="24px"
                      style={{ marginBottom: '16px' }}
                      cursor="pointer"
                      onClick={() => handleDeleteCohort(index, true)}
                    />
                    {cohortDetailsList.length > 1 && (
                      <SvgDelete
                        height="24px"
                        width="24px"
                        style={{ marginBottom: '16px', marginLeft: '8px' }}
                        cursor="pointer"
                        onClick={() => {
                          handleToggleList(index);
                          handleDeleteCohort(index, false);
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {/* AND/OR Toggle */}
            {cohortDetailsList.length > 1 &&
              index !== cohortDetailsList.length - 1 && (
                <Grid style={{ margin: '16px 0px' }}>
                  <ToggleGroup
                    leftValue={constants.AND}
                    rightValue={constants.OR}
                    index={index}
                    handleIndexChange={(i, toggleValue) => {
                      if (i === undefined) {
                        return;
                      }
                      handleToggleChange(i, toggleValue);
                    }}
                    showBothLabel={true}
                    toggleLeftSideStyles={toggleStyles}
                    toggleRightSideStyles={toggleStyles}
                    leftIcon={undefined}
                    rightIcon={undefined}
                    setSelectedToggle={() => {}}
                    value={
                      cohortDetailsStore.toggleList[index]?.toggleValue === 'or'
                        ? constants.OR
                        : constants.AND
                    }
                  />
                </Grid>
              )}
          </Grid>
        ))}
      </Box>
    );
  },
);

export default ReportsCohortItem;
